"use client";

import posthog from "posthog-js";
import type { ErrorContext } from "@/lib/errors/types";
import { derivePortalFromPath } from "@/lib/errors/portal-detect";

/**
 * Client-side companion to the server-side `logError`. Emits the same
 * `server_error` event shape as `PostHogAdapter.captureError` so the
 * single PostHog → Slack destination filter
 * (`event = 'server_error' AND severity IN ('error','critical')`) covers
 * both server-thrown and React-boundary-thrown errors.
 *
 * Also fans out to:
 *  - `posthog.captureException` — feeds the PostHog Error Tracking
 *    dashboard and benefits from the project's source-map upload (custom
 *    events don't get symbolicated).
 *  - `console.error` — Railway log fallback. Important because `global-error.tsx`
 *    can fire before the PostHogProvider mounts, in which case the capture
 *    above is buffered/dropped and console is our only signal.
 *
 * Pathname + portal are auto-derived from `window.location` so call sites
 * stay terse and every captured error gets multi-tenant context.
 *
 * Plan ref: §5 Phase 4.
 */
export function captureClientError(
  error: Error & { digest?: string },
  context?: ErrorContext,
): void {
  // Always log to console — never depend solely on a network call from a
  // boundary that may have failed before the reporter initialised.
  // eslint-disable-next-line no-console
  console.error("[client-error]", error);

  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;

  // Auto-derive multi-tenant context from the URL when callers don't
  // supply it. Safe inside "use client" — boundaries always run in the
  // browser.
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : undefined;
  const portal =
    context?.portal ??
    (pathname ? derivePortalFromPath(pathname) : undefined);

  const {
    severity = "error",
    category,
    errorCode,
    operation,
    portal: _portalIgnored,
    ...rest
  } = context ?? {};

  const properties = {
    error_name: error.name,
    error_message: error.message,
    error_stack: error.stack,
    error_digest: error.digest,
    severity,
    category,
    errorCode,
    operation,
    portal,
    pathname,
    ...rest,
  };

  posthog.capture("server_error", properties);

  // Also feed PostHog's Error Tracking product. captureException is what
  // gets symbolicated by the source-map upload integration; without this
  // call, stacks in the Error Tracking dashboard would stay minified.
  posthog.captureException(error, {
    errorCode,
    category,
    severity,
    operation,
    portal,
    pathname,
    error_digest: error.digest,
  });
}
